import React, { useCallback, useEffect, useId, useState, type ChangeEvent } from "react";
import CustomButton from "../../../../../components/common/general/CustomButton/CustomButton";
import { Modal, Platform, Pressable, StyleSheet, TextInput, View } from "react-native";
import { SparkleIcon, XIcon } from "@phosphor-icons/react";
import { useGenerateModernizationAiRewriteSuggestionMutation } from "@axio/graphql/generated/graphql";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { useFocusTrap } from "../../../../../hooks/useFocusTrap";
import { colors, radii, spacing, typographyScale } from "../../../../../styles/design-system";

interface AIRewriteActionButtonProps {
  disabled?: boolean;
  onPress: () => void;
}

interface AIRewriteModalProps {
  errorMessage: string | null;
  isRewriting: boolean;
  onCancel: () => void;
  onRewrite: (instructions: string) => void;
  visible: boolean;
}

type UseAIRewriteSuggestionArgs = {
  currentText?: string;
  getProposedText: () => string;
  onApplyRewrite: (rewrittenText: string) => void;
  title?: string;
};

export function useAIRewriteSuggestion({
  currentText,
  getProposedText,
  onApplyRewrite,
  title,
}: UseAIRewriteSuggestionArgs) {
  const [isRewriteModalVisible, setIsRewriteModalVisible] = useState(false);
  const [rewriteErrorMessage, setRewriteErrorMessage] = useState<string | null>(null);
  const [generateRewriteSuggestion, { loading: isRewriting }] = useGenerateModernizationAiRewriteSuggestionMutation();

  const openRewriteModal = useCallback(() => {
    setRewriteErrorMessage(null);
    setIsRewriteModalVisible(true);
  }, []);

  const closeRewriteModal = useCallback(() => {
    if (isRewriting) return;
    setIsRewriteModalVisible(false);
    setRewriteErrorMessage(null);
  }, [isRewriting]);

  const submitRewrite = useCallback(
    async (instructions: string) => {
      setRewriteErrorMessage(null);

      try {
        const response = await generateRewriteSuggestion({
          variables: {
            input: {
              currentText,
              proposedText: getProposedText(),
              instructions,
              title,
            },
          },
        });
        const result = response.data?.generateModernizationAiRewriteSuggestion;

        if (!result?.success || !result.rewrittenText) {
          throw new Error(result?.message ?? "Unable to generate rewrite suggestion.");
        }

        const rewrittenText = result.rewrittenText;
        onApplyRewrite(rewrittenText);
        setIsRewriteModalVisible(false);
      } catch (error) {
        setRewriteErrorMessage(error instanceof Error ? error.message : "We couldn't generate a rewrite. Try again.");
      }
    },
    [currentText, generateRewriteSuggestion, getProposedText, onApplyRewrite, title]
  );

  return {
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    closeRewriteModal,
    submitRewrite,
  };
}

export function AIRewriteActionButton({ disabled = false, onPress }: AIRewriteActionButtonProps) {
  return (
    <Pressable
      accessibilityRole="button"
      accessibilityLabel="AI Rewrite"
      accessibilityState={{ disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[styles.actionButton, disabled && styles.actionButtonDisabled]}>
      <SparkleIcon size={12} color={disabled ? colors.grey[400] : colors.grey[700]} weight="regular" />
      <CustomText
        text="AI Rewrite"
        useTranslationText={false}
        size="xs"
        style={[styles.actionButtonText, disabled && styles.actionButtonTextDisabled]}
      />
    </Pressable>
  );
}

export function AIRewriteModal({ errorMessage, isRewriting, onCancel, onRewrite, visible }: AIRewriteModalProps) {
  const [instructions, setInstructions] = useState("");
  const [isInstructionsFocused, setIsInstructionsFocused] = useState(false);
  const uniqueId = useId();
  const titleId = `ai-rewrite-modal-title-${uniqueId}`;
  const { trapRef: modalContentRef } = useFocusTrap({ visible, onEscape: onCancel });
  const canRewrite = instructions.trim().length > 0 && !isRewriting;

  useEffect(() => {
    if (!visible) {
      setInstructions("");
      setIsInstructionsFocused(false);
    }
  }, [visible]);

  const handleRewrite = useCallback(() => {
    if (!canRewrite) return;
    onRewrite(instructions.trim());
  }, [canRewrite, instructions, onRewrite]);

  return (
    <Modal
      role="dialog"
      aria-modal={true}
      aria-labelledby={titleId}
      accessibilityViewIsModal={true}
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View ref={modalContentRef} style={styles.modalCard}>
          <View style={styles.modalHeader}>
            <CustomText
              nativeID={titleId}
              role="heading"
              accessibilityRole="header"
              aria-level={2}
              text="AI Rewrite"
              useTranslationText={false}
              weight="bold"
              style={styles.modalTitle}
            />
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Close"
              disabled={isRewriting}
              onPress={onCancel}
              style={styles.closeButton}>
              <XIcon size={16} color={colors.grey[500]} weight="regular" />
            </Pressable>
          </View>
          <CustomText
            text="Tell the AI how you'd like this content rewritten. Be as specific as you'd like."
            useTranslationText={false}
            weight="normal"
            style={styles.helperText}
          />
          {Platform.OS === "web" ? (
            <textarea
              aria-label="AI rewrite instructions"
              autoFocus
              className="ai-rewrite-instructions-textarea"
              disabled={isRewriting}
              onBlur={() => setIsInstructionsFocused(false)}
              onChange={(event: ChangeEvent<HTMLTextAreaElement>) => setInstructions(event.target.value)}
              onFocus={() => setIsInstructionsFocused(true)}
              placeholder="e.g., Make it more concise, adjust the tone to be more student-friendly, add specific office hours for MWF 2-3pm..."
              style={{
                width: "100%",
                minHeight: 100,
                padding: 12,
                appearance: "none",
                WebkitAppearance: "none",
                color: colors.grey[900],
                backgroundColor: colors.white,
                borderColor: isInstructionsFocused ? colors.brand[400] : colors.grey[300],
                borderRadius: radii.md,
                borderStyle: "solid",
                borderWidth: 1,
                boxShadow: "none",
                boxSizing: "border-box",
                fontFamily: "Inter, system-ui, sans-serif",
                fontSize: typographyScale.textSm.fontSize,
                lineHeight: `${typographyScale.textSm.lineHeight}px`,
                outline: "none",
                resize: "vertical",
              }}
              value={instructions}
            />
          ) : (
            <TextInput
              value={instructions}
              onChangeText={setInstructions}
              onBlur={() => setIsInstructionsFocused(false)}
              onFocus={() => setIsInstructionsFocused(true)}
              multiline
              autoFocus
              editable={!isRewriting}
              placeholder="e.g., Make it more concise, adjust the tone to be more student-friendly, add specific office hours for MWF 2-3pm..."
              placeholderTextColor="#667085"
              style={[styles.instructionsInput, isInstructionsFocused && styles.instructionsInputFocused]}
              textAlignVertical="top"
            />
          )}
          {errorMessage && (
            <CustomText text={errorMessage} useTranslationText={false} size="xs" style={styles.errorText} />
          )}
          <View style={styles.modalActions}>
            <Pressable
              accessibilityRole="button"
              disabled={isRewriting}
              onPress={onCancel}
              style={[styles.cancelButton, isRewriting && styles.modalButtonDisabled]}>
              <CustomText text="Cancel" useTranslationText={false} size="s" style={styles.cancelButtonText} />
            </Pressable>
            <CustomButton
              text={isRewriting ? "Rewriting..." : "Rewrite"}
              useTranslationText={false}
              size="s"
              styleType="primary"
              leftIcon={<SparkleIcon size={14} color={colors.white} weight="regular" />}
              onPress={handleRewrite}
              disabled={!canRewrite}
              style={[styles.rewriteButtonSize, !canRewrite && styles.rewriteButtonDisabled]}
              textStyle={styles.rewriteButtonText}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    minHeight: spacing.xl,
    paddingHorizontal: 12,
    borderRadius: radii.sm,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  actionButtonDisabled: {
    backgroundColor: colors.grey[50],
    opacity: 0.7,
  },
  actionButtonText: {
    color: colors.grey[700],
  },
  actionButtonTextDisabled: {
    color: colors.grey[400],
  },
  overlay: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.72)",
    paddingHorizontal: spacing.md,
  },
  modalCard: {
    width: "100%",
    maxWidth: 448,
    borderRadius: radii.lg,
    backgroundColor: colors.white,
    padding: spacing.lg,
    shadowColor: colors.black,
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.1,
    shadowRadius: 15,
    elevation: 8,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing.md,
  },
  modalTitle: {
    color: colors.grey[900],
    fontSize: typographyScale.textLg.fontSize,
    fontWeight: "600",
    lineHeight: typographyScale.textLg.lineHeight,
  },
  closeButton: {
    padding: spacing.xxs,
  },
  helperText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    marginBottom: 12,
  },
  instructionsInput: {
    minHeight: 100,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: radii.md,
    color: colors.grey[900],
    fontFamily: "Inter",
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
    paddingHorizontal: 12,
    paddingVertical: 12,
  },
  instructionsInputFocused: {
    borderColor: colors.brand[400],
  },
  errorText: {
    color: colors.error[600],
    marginTop: spacing.xs,
  },
  modalActions: {
    flexDirection: "row",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: spacing.xs,
    marginTop: 12,
  },
  cancelButton: {
    minHeight: spacing.xl,
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: radii.md,
    backgroundColor: colors.white,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    color: colors.grey[700],
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  rewriteButtonSize: {
    minHeight: spacing.xl,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
    borderRadius: radii.md,
    paddingHorizontal: 12,
  },
  rewriteButtonDisabled: {
    opacity: 0.5,
  },
  rewriteButtonText: {
    color: colors.white,
    fontSize: typographyScale.textSm.fontSize,
    lineHeight: typographyScale.textSm.lineHeight,
  },
  modalButtonDisabled: {
    opacity: 0.7,
  },
});
